/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useState } from 'react';

import { requestTimeSeriesDataset } from '@/api/eeCheckApi';
import TimeSeriesValueCard from '@/components/kpi/TimeSeriesValueCard';
import TimeSeriesValueCarousel from '@/components/kpi/TimeSeriesValueCarousel';
import { useI18n } from '@/context/I18nContext';
import { useSession } from '@/context/SessionContext';
import { isExpiredSessionError } from '@/lib/app/debugRequests';
import { formatKpiDateTime } from '@/lib/formatting/dateTime';
import {
    getStatusClassName,
    getStatusSeverityFromTrafficLight,
} from '@/lib/overview/overviewCards';
import {
    getTimeSeriesSensorNotFoundMessage,
    getTimeSeriesSensorNotNumericMessage,
} from '@/lib/sensors/presentation/sensorRequestMessages';
import { parseTimeSeriesCsv } from '@/lib/timeSeries/parseTimeSeriesCsv';
import { resolveAvailablePeriodLoss, resolveCalendarLoss, type CalendarLossPeriod } from '@/lib/timeSeries/calendarLoss';
import {
    aggregateTimeSeriesValues,
    findTimeSeriesValueColumn,
    getTimeSeriesAggregationDate,
    normalizeTimeSeriesUrn,
    resolveTimeSeriesLookbackDifference,
    resolveTimeSeriesTrafficLightSeverity,
    type TimeSeriesAggregation,
    type TimeSeriesLookback,
    type TimeSeriesTrafficLightConfig,
} from '@/lib/timeSeries/timeSeriesValue';
import type { TimeSeriesRequest } from '@/types/eeCheck';

export interface KpiCarouselTrafficLightContext {
    comparisonDate: string | null;
    value: number;
    valueDate: string | null;
    values: Record<string, number | null>;
}

interface KpiCarouselApi2TimeSeriesModalConfig {
    buttonLabel?: string;
    message: string;
    title?: string;
}

export interface KpiCarouselApi2TimeSeriesItem {
    calendarPeriod?: CalendarLossPeriod;
    useAvailablePeriodValues?: boolean;
    aggregation: TimeSeriesAggregation;
    conversion?: number;
    fractionDigits?: number;
    key: string;
    legendCode?: string;
    legendConversion?: number;
    legendFractionDigits?: number;
    legendNote?: string;
    legendOffset?: number;
    legendUnit?: string | null;
    lookback?: TimeSeriesLookback;
    noDataMessage?: string;
    offset?: number;
    resolveUnit?: (value: number) => string | null;
    series?: string;
    title?: string;
    trafficLight?: TimeSeriesTrafficLightConfig;
    resolveTrafficLight?: (context: KpiCarouselTrafficLightContext) => TimeSeriesTrafficLightConfig;
    resolveTrafficLightValue?: (context: KpiCarouselTrafficLightContext) => number | null;
    trimFractionDigitsForIntegers?: boolean;
    transformValue?: (value: number) => number;
    trimTrailingZeros?: boolean;
    unit?: string | null;
    urn: string | string[];
}

interface ResolvedCarouselItem {
    evaluatedPeriod?: CalendarLossPeriod;
    comparisonDate: string | null;
    errorMessage: string;
    key: string;
    trafficLight?: TimeSeriesTrafficLightConfig;
    trafficLightValue?: number | null;
    value: number | null;
    valueDate: string | null;
}

function getItemUrns(urn: string | string[]): string[] {
    return normalizeTimeSeriesUrn(urn)
        .split(',')
        .map((itemUrn) => itemUrn.trim())
        .filter(Boolean);
}

interface KpiCarouselApi2TimeSeriesProps {
    autoAdvanceMs?: number;
    debug?: boolean;
    from: string;
    info?: string;
    items: KpiCarouselApi2TimeSeriesItem[];
    link?: string;
    modal?: KpiCarouselApi2TimeSeriesModalConfig;
    showDate?: boolean;
    showLegend?: boolean;
    to: string;
}

function createErrorItems(
    items: KpiCarouselApi2TimeSeriesItem[],
    errorMessage: string,
): ResolvedCarouselItem[] {
    return items.map((item) => ({
        comparisonDate: null,
        key: item.key,
        errorMessage,
        value: null,
        valueDate: null,
    }));
}

function KpiCarouselApi2TimeSeries({
    autoAdvanceMs,
    debug = false,
    from,
    info,
    items,
    link,
    modal,
    showDate = false,
    showLegend = false,
    to,
}: KpiCarouselApi2TimeSeriesProps) {
    const { locale, t } = useI18n();
    const { accessToken, isSessionExpired, onSessionExpired } = useSession();
    const [resolvedItems, setResolvedItems] = useState<ResolvedCarouselItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingLabel, setLoadingLabel] = useState('');
    const itemsSignature = JSON.stringify(items);

    useEffect(() => {
        if (isSessionExpired) {
            setIsLoading(false);
            return;
        }

        if (!accessToken || items.length === 0 || !from || !to) {
            setResolvedItems(
                createErrorItems(items, t('Missing time series request parameters.')),
            );
            setIsLoading(false);
            return;
        }

        const requestUrn = Array.from(
            new Set(
                items
                    .flatMap((item) => getItemUrns(item.urn)),
            ),
        ).join(',');
        const payload: TimeSeriesRequest = {
            from,
            to,
            urn: requestUrn,
        };

        let isCancelled = false;

        async function loadValues(): Promise<void> {
            setIsLoading(true);
            setLoadingLabel(t('Loading API request...'));

            try {
                const { data } = await requestTimeSeriesDataset(accessToken, payload, ({ currentRequest, totalRequests }) => {
                    setLoadingLabel(
                        totalRequests > 1
                            ? t('Loading API request {current}/{total}', {
                                current: String(currentRequest),
                                total: String(totalRequests),
                            })
                            : t('Loading API request...'),
                    );
                });

                if (isCancelled) {
                    return;
                }

                if (typeof data !== 'string') {
                    setResolvedItems(
                        createErrorItems(items, t('Time series response is not a CSV string.')),
                    );
                    return;
                }

                const parsedTimeSeries = parseTimeSeriesCsv(data);
                const nextResolvedItems = items.map((item) => {
                    const itemUrns = getItemUrns(item.urn);
                    const selectedSeries = item.series ?? itemUrns[0] ?? '';
                    const valueColumn = findTimeSeriesValueColumn(
                        parsedTimeSeries,
                        selectedSeries,
                    );

                    if (!valueColumn) {
                        return {
                            key: item.key,
                            errorMessage: getTimeSeriesSensorNotFoundMessage(
                                t,
                                selectedSeries,
                            ),
                            value: null,
                            valueDate: null,
                            comparisonDate: null,
                        };
                    }

                    const values = parsedTimeSeries.plotYAxisValues[valueColumn] ?? [];
                    const lookbackDifference = item.lookback
                        ? resolveTimeSeriesLookbackDifference(
                            values,
                            parsedTimeSeries.plotXAxisValues,
                            item.lookback,
                        )
                        : null;
                    const availablePeriodLoss = item.calendarPeriod && item.useAvailablePeriodValues
                        ? resolveAvailablePeriodLoss(values, parsedTimeSeries.plotXAxisValues, item.calendarPeriod)
                        : null;
                    const aggregatedValue = item.useAvailablePeriodValues
                        ? availablePeriodLoss?.value ?? null
                        : item.calendarPeriod
                        ? resolveCalendarLoss(values, parsedTimeSeries.plotXAxisValues, item.calendarPeriod)
                        : lookbackDifference?.value ?? (
                        item.lookback
                            ? null
                            : aggregateTimeSeriesValues(values, item.aggregation)
                    );

                    if (aggregatedValue === null) {
                        return {
                            key: item.key,
                            errorMessage: item.noDataMessage ?? getTimeSeriesSensorNotNumericMessage(
                                t,
                                selectedSeries,
                            ),
                            value: null,
                            valueDate: null,
                            comparisonDate: null,
                        };
                    }

                    const trafficLightValues = Object.fromEntries(
                        itemUrns.map((itemUrn) => {
                            const itemValueColumn = findTimeSeriesValueColumn(
                                parsedTimeSeries,
                                itemUrn,
                            );
                            const itemValues = itemValueColumn
                                ? parsedTimeSeries.plotYAxisValues[itemValueColumn] ?? []
                                : [];

                            return [
                                itemUrn,
                                aggregateTimeSeriesValues(itemValues, item.aggregation),
                            ];
                        }),
                    );
                    const valueDate = getTimeSeriesAggregationDate(
                        values,
                        parsedTimeSeries.plotXAxisValues,
                        item.aggregation,
                    );

                    return {
                        key: item.key,
                        errorMessage: '',
                        value: aggregatedValue,
                        evaluatedPeriod: availablePeriodLoss?.period,
                        comparisonDate: lookbackDifference?.referenceDate ?? null,
                        trafficLight: item.resolveTrafficLight?.({
                            comparisonDate: lookbackDifference?.referenceDate ?? null,
                            value: aggregatedValue,
                            valueDate,
                            values: trafficLightValues,
                        }),
                        trafficLightValue: item.resolveTrafficLightValue?.({
                            comparisonDate: lookbackDifference?.referenceDate ?? null,
                            value: aggregatedValue,
                            valueDate,
                            values: trafficLightValues,
                        }),
                        valueDate: showDate ? valueDate : null,
                    };
                });

                setResolvedItems(nextResolvedItems);
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                if (isExpiredSessionError(error)) {
                    onSessionExpired();
                }

                setResolvedItems(
                    createErrorItems(
                        items,
                        error instanceof Error
                            ? error.message
                            : t('Time series request failed.'),
                    ),
                );
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                    setLoadingLabel('');
                }
            }
        }

        void loadValues();

        return () => {
            isCancelled = true;
        };
    }, [accessToken, from, isSessionExpired, items, itemsSignature, onSessionExpired, showDate, t, to]);

    return (
        <TimeSeriesValueCarousel
            autoAdvanceMs={autoAdvanceMs}
            isLoading={isLoading}
            items={items.map((item, index) => {
                const resolvedItem = resolvedItems[index];
                const trafficLight = resolvedItem?.trafficLight ?? item.trafficLight;
                const trafficLightSeverity = resolveTimeSeriesTrafficLightSeverity(
                    resolvedItem?.trafficLightValue ?? resolvedItem?.value ?? null,
                    trafficLight,
                );

                return {
                    className: !debug && trafficLightSeverity
                        ? getStatusClassName(getStatusSeverityFromTrafficLight(trafficLightSeverity))
                        : undefined,
                    key: item.key,
                    content: (
                        <TimeSeriesValueCard
                            conversion={debug ? 1 : (item.conversion ?? 1)}
                            displayValue={
                                debug && resolvedItem?.value !== null
                                    ? String(resolvedItem.value)
                                    : null
                            }
                            errorMessage={resolvedItem?.errorMessage ?? ''}
                            fractionDigits={item.trimTrailingZeros ? undefined : (item.fractionDigits ?? 2)}
                            info={debug ? undefined : info}
                            legendConversion={debug ? 1 : item.legendConversion}
                            legendCode={debug || !showLegend ? undefined : item.legendCode}
                            legendFractionDigits={debug ? undefined : item.legendFractionDigits}
                            legendNote={debug || !showLegend ? undefined : item.legendNote}
                            legendOffset={debug ? 0 : item.legendOffset}
                            legendSensor={debug ? undefined : (item.series ?? getItemUrns(item.urn)[0])}
                            legendUnit={debug ? null : item.legendUnit}
                            link={link}
                            metaText={debug
                                ? null
                                : (
                                    item.calendarPeriod
                                        ? t('Period: {from} – {to}', {
                                            from: new Date((resolvedItem?.evaluatedPeriod?.from ?? item.calendarPeriod.from).replace(' ', 'T')).toLocaleDateString(locale),
                                            to: new Date(resolvedItem?.evaluatedPeriod
                                                ? resolvedItem.evaluatedPeriod.to.replace(' ', 'T')
                                                : new Date(item.calendarPeriod.to.replace(' ', 'T')).getTime() - 1).toLocaleDateString(locale),
                                        })
                                        : item.lookback && resolvedItem?.comparisonDate
                                        ? t('Comparison value from {date}', {
                                            date: formatKpiDateTime(resolvedItem.comparisonDate, locale),
                                        })
                                        : null
                                )}
                            metaDate={debug || item.lookback || item.calendarPeriod
                                ? null
                                : (showDate ? resolvedItem?.valueDate ?? null : null)}
                            modal={modal}
                            offset={debug ? 0 : (item.offset ?? 0)}
                            resolveUnit={debug ? undefined : item.resolveUnit}
                            showLegend={debug ? false : showLegend}
                            title={item.title}
                            trafficLight={debug ? undefined : trafficLight}
                            trafficLightValue={debug ? undefined : resolvedItem?.trafficLightValue}
                            trimFractionDigitsForIntegers={item.trimFractionDigitsForIntegers}
                            transformValue={debug ? undefined : item.transformValue}
                            unit={debug ? null : item.unit}
                            value={resolvedItem?.value ?? null}
                        />
                    ),
                };
            })}
            loadingLabel={loadingLabel}
            manualPauseMs={60000}
        />
    );
}

export default KpiCarouselApi2TimeSeries;
